import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Chip, Table, TableBody, TableCell, TableContainer, TableRow } from "@mui/material";
import MainCard from "components/MainCard";
import { getEquipmentsCategoriesById } from "components/axios/equipmentAlertsAxios";
import ComponentStatusDiv from "../ComponentStatusDiv";
import { useEnumApi } from "contexts/EnumApiContext";
import { equipmentFaultStatusColors, getSettledValue, groupAndDeduplicate } from "data/data";
import EquipmentRemarkModifyLayer from "components/layer/EquipmentRemarkModifyLayer";
import { NavLink } from "react-bootstrap";
import EquipmentPartListMonitoring from "../EquipmentPartListMonitoring";
import EquipmentPanel from "../EquipmentPanel";
import { getEquipmentOpLogsTimeline, getEquipmentPartsWithSensorPositionByQuery, getLiftOpLogsQuery } from "components/axios/equipmentAxios";
import { useQuery } from "@tanstack/react-query";
import { getEquipmentPartsIncludingFaultsByEquipmentId } from "components/axios/masterEquipmentPartsImageAxios";
import PictureTab from "../equipment/PictureTab";
import useConfig from "hooks/useConfig";

const DELAY_TIME = Number(process.env.REACT_APP_DELAY_TIME) || 60000;
const EquipmentStatusCard = ({info, onRefresh}) => {
    const { getEnumValueByEnumNameAndCode } = useEnumApi();
    const { t } = useTranslation();
    const { mode } = useConfig();

    // const [equipmentComponents, setEquipmentComponents] = useState([]);
    const [componentId, setComponentId] = useState(null);
    const [selectedComponent, setSelectedComponent] = useState(null);
    // const [grade, setGrade] = useState();

    const { data, isFetching, refetch } = useQuery({
        queryKey: ['equipment-alert-view', info.id],

        queryFn: async () => {
            if (!info) return null;
            const [
                categoriesResult,
                partsWithSensorResult,
                partsIncludingFaultsResult,
            ] = await Promise.allSettled([
                getEquipmentsCategoriesById(info.id),
                getEquipmentPartsWithSensorPositionByQuery({
                    "equipment.id": info.id,
                    deletedYn: false
                }),
                getEquipmentPartsIncludingFaultsByEquipmentId(info.id),
            ]);

            return {
                categories: getSettledValue(categoriesResult),
                partsWithSensor: getSettledValue(partsWithSensorResult, []),
                partsIncludingFaults: getSettledValue(partsIncludingFaultsResult, []),
            };
        },

        // 깜빡임 방지
        placeholderData: previousData => previousData,

        // 🔥 기존 polling 대체
        refetchInterval: DELAY_TIME,

        refetchOnWindowFocus: false,
        refetchOnReconnect: false,

        // 🔥 탭 안 보여도 멈추지 않음(기본값은 false)
        refetchIntervalInBackground: true,

        enabled: !!info
    });

    // 1번 탭 데이터 존재 여부 체크
    const { data: controlCheckData, isFetched: isControlChecked } = useQuery({
        queryKey: ['liftOpControlLogs', 'check', info.id, { page: 0, size: 1 }],
        queryFn: () => getLiftOpLogsQuery({ page: 0, size: 1, "equipmentId": info.id }),
        // queryFn: () => getLiftOpLogsQuery({ page: 0, size: 1, "equipment.id": equipment?.id, "controlPanelCode.usrDfnCd": "isNotNull()" }),
        enabled: !!info,
        staleTime: 1000 * 30, // 30초 동안은 캐시 유지 (존재 여부 체크를 매번 DB 조회하지 않음)
        gcTime: 1000 * 60 * 5,
    });

    // 2번 탭 데이터 존재 여부 체크
    const { data: timelineCheckData, isFetched: isTimelineChecked } = useQuery({
        queryKey: ['liftOperationLogs', 'check', info.id, { page: 0, size: 1 }],
        queryFn: () => getEquipmentOpLogsTimeline(info.id, { page: 0, size: 1 }),
        enabled: !!info,
        staleTime: 1000 * 30, // 30초 동안은 캐시 유지 (존재 여부 체크를 매번 DB 조회하지 않음)
        gcTime: 1000 * 60 * 5,
    });

    const hasControlData  = (controlCheckData?.totalElements ?? 0) > 0;
    const hasTimelineData = (timelineCheckData?.page?.totalElements ?? 0) > 0;

    const equipmentPartsWithSensorPosition = useMemo(() => {
        return groupAndDeduplicate(
        data?.partsWithSensor ?? [],
        {
            groupBy: [
                "masterEquipmentPart.equipmentCtgr.id",
                "componentLocation.id",
                "componentLocation.code",
                "componentLocation.name"
            ],
            dedupeBy: []
        }
        );
    }, [data?.partsWithSensor]);

    const equipmentComponents = data?.categories;
    const equipmentComponentList = data?.partsIncludingFaults;

    // 해당 구성부에 해당하는 자재목록
    const equipmentPartsByComponentId = (compId) => {
        setComponentId(compId);

        if (compId > 0) {
            setSelectedComponent(
            equipmentComponentList.find(
                component => component.componentId === compId
            )
            )
        }
    }  

    const [previewImage, setPreviewImage] = useState('');
    const [extension, setExtension] = useState("png");
    const [selectedImgId, setSelectedImgId] = useState(null);
    const [selectedStream, setSelectedStream] = useState(null);
    
    const initDefaultImage = useCallback(() => {
        setSelectedStream(null);
    }, []);

    const setImageFromFile = useCallback((file, selectIdx) => {
        initDefaultImage();
        setPreviewImage(window.RUNTIME_CONFIG.REACT_APP_API_URL+"/api/download/"+file?.id);
        setExtension("png");
        if (selectIdx <= (info?.imgFiles?.length ?? 0)) {
            let copy = Array(info?.imgFiles?.length ?? 0).fill("");
            copy[selectIdx-1] = " on";
            setSelectedImgId(file.id)
        } else {
            setSelectedImgId(file.id)
        }
    }, [info, initDefaultImage]);

    const setDefaultImageFromFile = useCallback((selectIdx) => {
        initDefaultImage();
        setPreviewImage(mode === 'light' ? process.env.PUBLIC_URL+"/img/sample1.png" : process.env.PUBLIC_URL+"/img/sample.png")
        setExtension("png");
        if (selectIdx <= (info?.imgFiles?.length ?? 0)) {
            let copy = Array(info?.imgFiles?.length ?? 0).fill("");
            copy[selectIdx-1] = " on";
        } else {
        }
    }, [info, mode, initDefaultImage]);

    useEffect(()=>{
        if (info) {
        let copy = Array(info.imgFiles?.length).fill("");
        if (info?.imgFiles?.length > 0) {
            // 선택된 항목이 사라졌을 때만 초기화
            if (!info.imgFiles.some(v => v.id === selectedImgId) && info.bpFile?.id !== selectedImgId) {
            setImageFromFile(info.imgFiles[0], 1);
            copy[0] = " on";
            }
        } else {
            setPreviewImage(mode === 'light' ? process.env.PUBLIC_URL+"/img/sample1.png" : process.env.PUBLIC_URL+"/img/sample.png")
        }
        }
    }, [info?.imgFiles, info?.bpFile, selectedImgId, mode, setImageFromFile]);

    return (
        <>
            <div className='alert_phrase'>
                {info && 
                <React.Fragment>
                <MainCard className="center_table" content={false}>
                    <div className="statuscard_tit" style={{display:"flex", justifyContent:"space-between", backgroundColor:"#353535"}}>
                        <div style={{marginBottom:"10px"}}>
                            <span className='bullet'><img src={`${process.env.PUBLIC_URL}/img/icon/subway1.png`} alt='' /></span>&nbsp;
                            <Chip label={t('message.common.chip.equipment.status.status')} color="success" />
                        </div>

                        <NavLink href={`/equipment/${info?.id}`}><Button variant="contained">{t('message.common.btn.equipment.status.view')}</Button></NavLink>
                    </div>
                    <TableContainer>
                        <Table sx={{ minWidth: 400 }} aria-label="simple table" className="same_width">
                            <TableBody>
                                <TableRow>
                                    <TableCell className="th" align="center">{t('message.common.subtitle.equipment.level2')}</TableCell>
                                    <TableCell align="center">{info && info.equipmentCtgr.parent.name}</TableCell>
                                    <TableCell className="th" align="center">{t('message.common.subtitle.equipment.level3')}</TableCell>
                                    <TableCell align="center">{info && info.equipmentCtgr.name}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell className="th" align="center">{t('message.common.subtitle.equipment.number')}</TableCell>
                                    <TableCell align="center">{info && info.equipmentNo}</TableCell>
                                    <TableCell className="th" align="center">{t('message.common.subtitle.equipment.name')}</TableCell>
                                    <TableCell align="center">{info && info.name}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell className="th" align="center">{getEnumValueByEnumNameAndCode('CompanyType', 'MAN')?.[0]?.title}</TableCell>
                                    <TableCell align="center">{info && info.managementCompany?.info?.name}</TableCell>
                                    <TableCell className="th" align="center">{t('message.common.subtitle.company.contact')}</TableCell>
                                    <TableCell align="center">{info && info.managementCompany?.info?.contact}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell className="th" align="center">{getEnumValueByEnumNameAndCode('CompanyType', 'MFR')?.[0]?.title}</TableCell>
                                    <TableCell align="center">{info && info.manufacturingCompany?.info?.name}</TableCell>
                                    <TableCell className="th" align="center">{t('message.common.subtitle.company.contact')}</TableCell>
                                    <TableCell align="center">{info && info.manufacturingCompany?.info?.contact}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell className="th" align="center">{getEnumValueByEnumNameAndCode('CompanyType', 'MNT')?.[0]?.title}</TableCell>
                                    <TableCell align="center">{info && info.maintenanceCompany?.info?.name}</TableCell>
                                    <TableCell className="th" align="center">{t('message.common.subtitle.company.contact')}</TableCell>
                                    <TableCell align="center">{info && info.maintenanceCompany?.info?.contact}</TableCell>
                                </TableRow>
                                {/* <TableRow>
                                    <TableCell className="th" align="center">{t('message.common.subtitle.mfCpnyNm')}</TableCell>
                                    <TableCell align="center" colSpan={3}>{info && info.mfCpnyNm}</TableCell>
                                </TableRow> */}
                                <TableRow>
                                    <TableCell className="th" align="center">{t('message.common.subtitle.madeYmd')}</TableCell>
                                    <TableCell align="center">{info && info.madeYmd}</TableCell>
                                    <TableCell className="th" align="center">{t('message.common.subtitle.position')}</TableCell>
                                    <TableCell align="center">{info && info.location}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell className="th" align="center">{t('message.common.subtitle.grade')}</TableCell>
                                    <TableCell align="center" colSpan={3}>{info?.lowestDiagnosisRank || '-'}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell className="th" align="center">{t('message.common.subtitle.remark')}</TableCell>
                                    <TableCell align="center" colSpan={3}>
                                        <div  style={{display:"flex"}}>
                                        <div style={{whiteSpace:"pre-line", textAlign:"left", width:"100%"}}>
                                            {info && info.remark}
                                        </div>
                                        <EquipmentRemarkModifyLayer equipmentId={info.id} onRefresh={onRefresh} />
                                        </div>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </MainCard>
                <br/>
                </React.Fragment>
                }

                {(equipmentComponents?.length > 0 || componentId || info?.latestEquipmentOp || hasControlData || hasTimelineData) &&
                <>
                <MainCard className="center_table" content={false}>
                    <TableContainer>
                        <Table sx={{ minWidth: 500 }} aria-label="simple table">
                            <TableBody>
                                <TableRow>
                                    <TableCell style={{textAlign:"left"}}>
                                        <ComponentStatusDiv equipment={info} equipmentComponents={equipmentComponents} componentId={componentId} equipmentPartsByComponentId={equipmentPartsByComponentId}
                                            hasControlData={hasControlData} hasTimelineData={hasTimelineData} isPannelLoaded={isControlChecked && isTimelineChecked}
                                        />
                                    </TableCell>
                                    <TableCell align="center" style={{width:"175px", fontSize:"15px"}}>
                                        {/* <span className={getDiagnosisGradeColor(grade)}>{t('message.common.subtitle.defect')} : {grade || '-'}</span> */}
                                        <span className={equipmentFaultStatusColors.find(data => data.code === info?.status)?.color}>{t('message.common.subtitle.defect')} : {getEnumValueByEnumNameAndCode('EquipmentFaultStatus',info?.status)?.[0]?.title || '-'}</span>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </MainCard>

                {/* 운전/진단 */}
                {/* {((info?.latestEquipmentOp) && componentId && componentId === -1) &&  */}
                {((info?.latestEquipmentOp || hasControlData || hasTimelineData) && componentId && componentId === -1) && info && 
                <EquipmentPanel equipment={info} />
                }
                {/* 사진/도면 */}
                {componentId && componentId === -3 && info &&
                <PictureTab info={info} previewImage={previewImage} extension={extension} selectedStream={selectedStream} 
                    selectedImgId={selectedImgId} onRefresh={refetch} setDefaultImageFromFile={setDefaultImageFromFile} setImageFromFile={setImageFromFile} />
                }
                {/* 구성부탭목록 */}
                {componentId && componentId > 0 && info &&
                <div className='alert_phrase'>
                    <EquipmentPartListMonitoring equipment={info} componentId={componentId} selectedComponent={selectedComponent} equipmentAlertIng={null} equipmentParts={equipmentPartsWithSensorPosition} target="alertView" />
                </div>
                }
                </>
                }
            </div>
        </>
    )
}

export default EquipmentStatusCard;